/**
 * FatturatoEffettivoTab — Tab dentro il cantiere per gestire le fatture
 * effettivamente emesse + incassi.
 *
 * Include:
 * - KPI riepilogo (previsto, emesso, incassato, da incassare)
 * - Tabella fatture del cantiere
 * - Form nuovo/modifica fattura con collegamento a fase Gantt + richiesta_id
 * - Sezione "Piattaforma esterna" (spazio configurazione, no simulazioni)
 */
import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, X, Pencil, Trash2, Save, Receipt, Paperclip, Link as LinkIcon } from "lucide-react";
import api from "@/api";
import AiExtractBox from "@/components/AiExtractBox";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));
const isoDate = () => new Date().toISOString().slice(0, 10);

const STATI_FATTURA = [
  { k: "da_emettere", l: "Da emettere", c: "bg-slate-200 text-slate-700" },
  { k: "emessa", l: "Emessa", c: "bg-blue-100 text-blue-800" },
  { k: "parzialmente_incassata", l: "Parz. incassata", c: "bg-amber-100 text-amber-800" },
  { k: "incassata", l: "Incassata", c: "bg-emerald-100 text-emerald-800" },
  { k: "scaduta", l: "Scaduta", c: "bg-rose-100 text-rose-800" },
  { k: "annullata", l: "Annullata", c: "bg-slate-100 text-slate-500 line-through" },
];

const EMPTY = {
  numero_fattura: "",
  data_emissione: isoDate(),
  importo_imponibile: 0,
  aliquota_iva: 22,
  aliquota_ritenuta: 0,
  costo: 0,
  cliente: "",
  cliente_ragione_sociale: "",
  attivita_id: "",
  richiesta_id: "",
  rapportini_ids: [],
  stato: "emessa",
  data_incasso_prevista: "",
  data_incasso_effettiva: "",
  importo_incassato: 0,
  note: "",
  allegato_fattura: null,
  allegati: [],
  piattaforma_esterna: "",
  codice_esterno: "",
};

export default function FatturatoEffettivoTab({ cantiereId, attivitaOptions = [], cantiere }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showConfig, setShowConfig] = useState(false);

  const { data: fatture = [], isLoading } = useQuery({
    queryKey: ["fatture", cantiereId],
    queryFn: () => api.get("/fatture", { params: { cantiere_id: cantiereId } }).then((r) => r.data),
    enabled: !!cantiereId,
  });

  const { data: richieste = [] } = useQuery({
    queryKey: ["richieste-fatt", cantiereId],
    queryFn: () => api.get("/fatturazione/richieste", { params: { cantiere_id: cantiereId } }).then((r) => r.data),
    enabled: !!cantiereId,
  });

  // Rapportini del cantiere → possono essere collegati alla fattura per spostare
  // la loro IVA dal Previsionale all'Effettiva
  const { data: rapportiniCantiere = [] } = useQuery({
    queryKey: ["rapportini-cant", cantiereId],
    queryFn: () => api.get("/rapportini", { params: { cantiere_id: cantiereId } }).then((r) => r.data),
    enabled: !!cantiereId,
  });

  // Se l'utente seleziona rapportini con imponibile > 0 e la fattura è ancora
  // vuota, precompila imponibile e aliquota IVA con la somma/media dei rapportini
  // selezionati (mantenendo lo stesso trattamento IVA per tipo_cliente).
  const applyRapportiniToForm = (ids) => {
    const selected = rapportiniCantiere.filter((r) => ids.includes(r.rapportino_id));
    if (selected.length === 0) {
      setForm((f) => ({ ...f, rapportini_ids: [] }));
      return;
    }
    const sumImp = selected.reduce((s, r) => s + Number(r.imponibile || 0), 0);
    // Aliquota: se tutti i rapportini hanno la stessa aliquota (stesso tipo_cliente)
    // la usiamo; altrimenti lasciamo l'attuale.
    const aliquote = new Set(selected.map((r) => r.iva_previsionale_calcolata?.aliquota_iva || 22));
    const uniformAliq = aliquote.size === 1 ? [...aliquote][0] : null;
    setForm((f) => ({
      ...f,
      rapportini_ids: ids,
      importo_imponibile: sumImp > 0 && !editing ? sumImp : f.importo_imponibile,
      aliquota_iva: uniformAliq != null && !editing ? uniformAliq : f.aliquota_iva,
    }));
  };

  // Anagrafica clienti — per autocomplete "Cliente" nella fattura in ingresso
  const { data: clientiList = [] } = useQuery({
    queryKey: ["clienti-anagrafica"],
    queryFn: () => api.get("/entita/clienti").then((r) => r.data),
    staleTime: 60_000,
  });

  const kpi = useMemo(() => {
    let em = 0, inc = 0, resid = 0, scad = 0, totaleLordo = 0;
    for (const f of fatture) {
      if (f.stato === "annullata" || f.stato === "da_emettere" || f.tipo === "ricevuta") continue;
      em += Number(f.importo_imponibile || 0);
      totaleLordo += Number(f.importo_totale || 0);
      inc += Number(f.importo_incassato || 0);
      resid += Number(f.importo_residuo || 0);
      if (f.stato === "scaduta") scad += Number(f.importo_residuo || 0);
    }
    const previsto = (attivitaOptions || []).reduce((s, a) => s + Number(a.importo || 0), 0);
    return {
      previsto,
      emesso: em,
      incassato: inc,
      da_incassare: resid,
      scadute: scad,
      diff: previsto - em,
      pct_realizzo: previsto > 0 ? (em / previsto * 100) : 0,
      pct_incasso: totaleLordo > 0 ? (inc / totaleLordo * 100) : 0,
    };
  }, [fatture, attivitaOptions]);

  const save = useMutation({
    mutationFn: async (data) => {
      const body = { ...data, cantiere_id: cantiereId };
      if (editing) return (await api.patch(`/fatture/${editing.fattura_id}`, body)).data;
      return (await api.post("/fatture", body)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Fattura aggiornata" : "Fattura registrata");
      qc.invalidateQueries({ queryKey: ["fatture", cantiereId] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ", cantiereId] });
      closeForm();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const del = useMutation({
    mutationFn: (id) => api.delete(`/fatture/${id}`).then((r) => r.data),
    onSuccess: () => {
      toast.success("Fattura eliminata");
      qc.invalidateQueries({ queryKey: ["fatture", cantiereId] });
      qc.invalidateQueries({ queryKey: ["cantiere-econ", cantiereId] });
    },
  });

  const openNew = () => {
    const tipo = cantiere?.tipologia_contratto_iva;
    const aliquote = {
      privato_22: [22, 0],
      privato_10: [10, 0],
      privato_10_ritenuta: [10, 11],
      condominio_10: [10, 0],
      condominio_10_ritenuta: [10, 4],
      azienda_22: [22, 0],
    };
    const [iva, ritenuta] = aliquote[tipo] || [22, 0];

    setEditing(null);
    setForm({ ...EMPTY, aliquota_iva: iva, aliquota_ritenuta: ritenuta });
    setOpen(true);
  };
  const openEdit = (f) => {
    setEditing(f);
    setForm({ ...EMPTY, ...f });
    setOpen(true);
  };
  const closeForm = () => { setOpen(false); setEditing(null); setForm(EMPTY); };

  const handleAllegato = (file) => {
    if (!file || file.size > 4 * 1024 * 1024) {
      if (file) toast.error("File troppo grande (max 4MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, allegato_fattura: reader.result }));
    reader.readAsDataURL(file);
  };

  const iva = (Number(form.importo_imponibile) || 0) * (Number(form.aliquota_iva) || 0) / 100;
  const totale = (Number(form.importo_imponibile) || 0) + iva;
  const ritenuta = Math.round((totale / 1.22) * (Number(form.aliquota_ritenuta) || 0)) / 100;
  const incassoPrevisto = totale - ritenuta;
  const costoEffettivo = Number(form.costo) || 0;
  const margine = (Number(form.importo_imponibile) || 0) - costoEffettivo;
  const marginePct = form.importo_imponibile > 0 ? (margine / Number(form.importo_imponibile) * 100) : 0;

  return (
    <div className="space-y-4" data-testid="fatturato-effettivo-tab">
      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <KPI label="Previsto (Gantt)" value={eur(kpi.previsto)} />
        <KPI label="Emesso" value={eur(kpi.emesso)} highlight="blue" />
        <KPI label="Incassato" value={eur(kpi.incassato)} highlight="emerald" />
        <KPI label="Da incassare" value={eur(kpi.da_incassare)} highlight="amber" />
        <KPI label="Scadute" value={eur(kpi.scadute)} highlight="rose" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <KPI label="% Realizzo (emesso/previsto)" value={`${kpi.pct_realizzo.toFixed(1)}%`} />
        <KPI label="% Incasso (incassato/emesso)" value={`${kpi.pct_incasso.toFixed(1)}%`} />
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">Cantiere</div>
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Receipt className="w-5 h-5 text-slate-500" /> Fatturato Effettivo
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowConfig((s) => !s)}
            data-testid="btn-toggle-config"
            className="inline-flex items-center gap-2 px-3 py-2 border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold rounded-sm"
          >
            <LinkIcon className="w-4 h-4" /> Config. piattaforma esterna
          </button>
          <button
            onClick={openNew}
            data-testid="btn-nuova-fattura"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white text-sm font-semibold rounded-sm"
          >
            <Plus className="w-4 h-4" /> Nuova fattura
          </button>
        </div>
      </div>

      {showConfig && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-sm" data-testid="config-piattaforma">
          <h3 className="font-semibold mb-2">Piattaforma esterna (predisposizione)</h3>
          <p className="text-xs text-slate-600 mb-3">
            Predisposizione per futura integrazione con piattaforma di fatturazione/contabilità.
            Nessuna piattaforma è attualmente collegata. Le fatture registrate qui restano manuali.
          </p>
          <div className="grid md:grid-cols-2 gap-3 text-xs">
            <div className="p-3 bg-white border border-slate-200 rounded-sm">
              <div className="font-bold mb-1">Modalità supportate (future)</div>
              <ul className="list-disc list-inside text-slate-600 space-y-0.5">
                <li>API REST diretta</li>
                <li>Webhook eventi in ingresso</li>
                <li>Import file (CSV/XML/JSON)</li>
                <li>Sincronizzazione periodica</li>
              </ul>
            </div>
            <div className="p-3 bg-white border border-slate-200 rounded-sm">
              <div className="font-bold mb-1">Stato attuale</div>
              <div className="text-slate-600">Nessun provider configurato. Contatta l'amministratore per attivare l'integrazione.</div>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="p-8 text-center text-sm text-slate-400">Caricamento…</div>
      ) : fatture.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-400 bg-white border border-slate-200 rounded-sm">
          Nessuna fattura registrata per questo cantiere.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-600 uppercase tracking-widest">
              <tr>
                <th className="text-left p-2">N° fattura / Data</th>
                <th className="text-left p-2">Cliente</th>
                <th className="text-right p-2">Totale</th>
                <th className="text-right p-2">Incassato</th>
                <th className="text-right p-2">Residuo</th>
                <th className="text-center p-2">Incasso prev.</th>
                <th className="text-center p-2">Stato</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {fatture.map((f) => {
                const st = STATI_FATTURA.find((s) => s.k === f.stato);
                return (
                  <tr key={f.fattura_id} className="border-t border-slate-100" data-testid={`fatt-row-${f.fattura_id}`}>
                    <td className="p-2">
                      <div className="font-semibold">{f.numero_fattura || <i className="text-slate-400">senza numero</i>}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{f.data_emissione}</div>
                    </td>
                    <td className="p-2 text-slate-600">{f.cliente_ragione_sociale || f.cliente}</td>
                    <td className="p-2 text-right font-mono font-bold">{eur(f.importo_totale)}</td>
                    <td className="p-2 text-right font-mono text-emerald-700">{eur(f.importo_incassato)}</td>
                    <td className={`p-2 text-right font-mono ${f.importo_residuo > 0 ? "text-amber-700" : "text-slate-500"}`}>{eur(f.importo_residuo)}</td>
                    <td className="p-2 text-center text-xs font-mono">{f.data_incasso_prevista || "—"}</td>
                    <td className="p-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider ${st?.c || ""}`}>{st?.l || f.stato}</span>
                    </td>
                    <td className="p-2 text-right">
                      <button onClick={() => openEdit(f)} className="p-1.5 text-slate-700 hover:bg-slate-100 rounded" data-testid={`btn-edit-fatt-${f.fattura_id}`}><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => window.confirm(`Eliminare fattura ${f.numero_fattura || ""}?`) && del.mutate(f.fattura_id)} className="p-1.5 text-rose-600 hover:bg-rose-50 rounded" data-testid={`btn-del-fatt-${f.fattura_id}`}><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeForm}>
          <div className="bg-white w-full max-w-3xl border border-slate-200 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <h2 className="font-display font-bold text-xl">{editing ? "Modifica fattura" : "Nuova fattura"}</h2>
              <button onClick={closeForm}><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Numero fattura" testid="ff-numero" value={form.numero_fattura} onChange={(v) => setForm({ ...form, numero_fattura: v })} />
                <Field label="Data emissione" type="date" value={form.data_emissione} onChange={(v) => setForm({ ...form, data_emissione: v })} />
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Stato</label>
                  <select value={form.stato} onChange={(e) => setForm({ ...form, stato: e.target.value })} data-testid="ff-stato" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                    {STATI_FATTURA.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Cliente</label>
                <input
                  value={form.cliente_ragione_sociale || form.cliente || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    const patch = { cliente_ragione_sociale: v, cliente: v };
                    const match = clientiList.find(
                      (c) => (c.ragione_sociale || "").toLowerCase() === v.toLowerCase()
                    );
                    if (match?.cliente_id) patch.cliente_id = match.cliente_id;
                    setForm({ ...form, ...patch });
                  }}
                  list="ff-clienti-datalist"
                  autoComplete="off"
                  data-testid="ff-cliente"
                  className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                />
                <datalist id="ff-clienti-datalist">
                  {clientiList.map((c) => (
                    <option key={c.cliente_id} value={c.ragione_sociale}>
                      {c.partita_iva ? `P.IVA ${c.partita_iva}` : ""}
                    </option>
                  ))}
                </datalist>
              </div>
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Imponibile €" type="number" step="0.01" testid="ff-imponibile" value={form.importo_imponibile} onChange={(v) => setForm({ ...form, importo_imponibile: parseFloat(v || 0) })} />
                <Field label="IVA %" type="number" step="0.1" value={form.aliquota_iva} onChange={(v) => setForm({ ...form, aliquota_iva: parseFloat(v || 0) })} />
                <Field label="Ritenuta %" type="number" step="0.1" value={form.aliquota_ritenuta ?? 0} onChange={(v) => setForm({ ...form, aliquota_ritenuta: parseFloat(v || 0) })} />
                <div className="p-2 bg-slate-50 border border-slate-200 rounded-sm text-xs">
                  <div className="overline">IVA calc. / Totale</div>
                  <div className="font-mono font-bold">{eur(iva)} / <span className="text-rose-700">{eur(totale)}</span></div>
                  <div className="mt-2">Ritenuta: <strong>{eur(ritenuta)}</strong></div>
                  <div>Incasso previsto: <strong className="text-emerald-700">{eur(incassoPrevisto)}</strong></div>
                </div>
              </div>

              {/* Costo sostenuto — come nelle bolle, per calcolare margine */}
              <div className="grid md:grid-cols-3 gap-3 bg-slate-50 border border-slate-200 rounded-sm p-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">
                    Costo sostenuto €
                    <span className="ml-1 text-[10px] text-slate-500 font-normal">
                      (default = imponibile)
                    </span>
                  </label>
                  <input
                    type="number" step="0.01"
                    value={form.costo}
                    onChange={(e) => setForm({ ...form, costo: parseFloat(e.target.value || 0) })}
                    data-testid="ff-costo"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm font-mono"
                    placeholder={String(form.importo_imponibile || 0)}
                  />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Margine €</div>
                  <div className={`font-display font-black text-xl mt-0.5 ${margine >= 0 ? "text-emerald-700" : "text-rose-700"}`} data-testid="ff-margine">
                    {eur(margine)}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">% Margine</div>
                  <div className={`font-display font-black text-xl mt-0.5 ${marginePct >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {form.importo_imponibile > 0 ? `${marginePct.toFixed(1)}%` : "—"}
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5 italic">
                    ricavo − costo sostenuto
                  </div>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Fase Gantt collegata</label>
                  <select value={form.attivita_id} onChange={(e) => setForm({ ...form, attivita_id: e.target.value })} data-testid="ff-attivita" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                    <option value="">— nessuna —</option>
                    {attivitaOptions.map((a) => <option key={a.attivita_id} value={a.attivita_id}>{a.nome} ({eur(a.importo)})</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Richiesta di fatturazione</label>
                  <select value={form.richiesta_id} onChange={(e) => setForm({ ...form, richiesta_id: e.target.value })} data-testid="ff-richiesta" className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm bg-white">
                    <option value="">— nessuna —</option>
                    {richieste.map((r) => <option key={r.richiesta_id} value={r.richiesta_id}>{(r.attivita_nome || r.richiesta_id) + " · " + r.stato}</option>)}
                  </select>
                </div>
              </div>

              {/* Rapportini da includere: sposta l'IVA dal Previsionale all'Effettiva */}
              {rapportiniCantiere.length > 0 && (
                <div className="border border-orange-200 bg-orange-50/50 rounded-sm p-3" data-testid="ff-rapportini-block">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <label className="text-xs font-bold uppercase tracking-widest text-orange-800">
                        Rapportini fatturati con questa fattura
                      </label>
                      <p className="text-[11px] text-slate-600 mt-0.5">
                        Selezionando i rapportini, la loro IVA passa dal <b>Previsionale</b> all'<b>Effettiva</b> con la stessa aliquota della fattura.
                      </p>
                    </div>
                    <span className="text-[10px] font-mono text-slate-600 whitespace-nowrap">
                      {form.rapportini_ids?.length || 0} selezionati
                    </span>
                  </div>
                  <div className="max-h-52 overflow-y-auto divide-y divide-orange-100 bg-white border border-orange-200 rounded-sm">
                    {rapportiniCantiere.map((r) => {
                      const disabled = r.fatturato && r.fattura_emessa_id !== editing?.fattura_id;
                      const selected = (form.rapportini_ids || []).includes(r.rapportino_id);
                      const aliq = r.iva_previsionale_calcolata?.aliquota_iva ?? 22;
                      const iva = r.iva_previsionale_calcolata?.importo_iva ?? 0;
                      const tc = r.tipo_cliente || "privato";
                      return (
                        <label
                          key={r.rapportino_id}
                          className={`flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed bg-slate-50" : selected ? "bg-orange-50" : "hover:bg-slate-50"}`}
                          data-testid={`ff-rap-${r.rapportino_id}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            disabled={disabled}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...(form.rapportini_ids || []), r.rapportino_id]
                                : (form.rapportini_ids || []).filter((x) => x !== r.rapportino_id);
                              applyRapportiniToForm(next);
                            }}
                            className="w-4 h-4"
                          />
                          <span className="font-mono w-24">{r.data}</span>
                          <span className="uppercase text-[9px] font-bold px-1.5 py-0.5 border rounded-sm border-slate-300 bg-slate-50 text-slate-700">
                            {tc}
                          </span>
                          <span className="flex-1 truncate">{r.lavorazioni || "—"}</span>
                          <span className="font-mono">{eur(r.imponibile)}</span>
                          <span className="font-mono text-orange-700">
                            IVA {aliq}% = {eur(iva)}
                          </span>
                          {disabled && (
                            <span className="text-[9px] font-bold text-rose-700">già in altra fattura</span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="grid md:grid-cols-3 gap-3">
                <Field label="Data incasso prevista" type="date" value={form.data_incasso_prevista} onChange={(v) => setForm({ ...form, data_incasso_prevista: v })} />
                <Field label="Data incasso effettiva" type="date" value={form.data_incasso_effettiva} onChange={(v) => setForm({ ...form, data_incasso_effettiva: v })} />
                <Field label="Importo incassato €" type="number" step="0.01" testid="ff-incassato" value={form.importo_incassato} onChange={(v) => setForm({ ...form, importo_incassato: parseFloat(v || 0) })} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 block mb-1">Note</label>
                <textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-slate-600 block mb-1">Allegato fattura (PDF/immagine)</label>
                  <input type="file" accept="application/pdf,image/*" onChange={(e) => handleAllegato(e.target.files?.[0])} data-testid="ff-allegato" className="w-full text-xs" />
                  {form.allegato_fattura && <div className="text-[11px] text-emerald-700 mt-1 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> Allegato caricato <button onClick={() => setForm({ ...form, allegato_fattura: null })} className="ml-2 text-rose-600 hover:underline">rimuovi</button></div>}
                  <AiExtractBox
                    tipo="fattura"
                    allegato={form.allegato_fattura}
                    onExtracted={(d) => setForm((f) => ({
                      ...f,
                      numero_fattura: d.numero_fattura || f.numero_fattura,
                      data_emissione: /^\d{4}-\d{2}-\d{2}$/.test(d.data_emissione) ? d.data_emissione : f.data_emissione,
                      cliente_ragione_sociale: d.cliente_ragione_sociale || f.cliente_ragione_sociale,
                      cliente: d.cliente_ragione_sociale || f.cliente,
                      importo_imponibile: d.importo_imponibile > 0 ? d.importo_imponibile : f.importo_imponibile,
                      aliquota_iva: d.aliquota_iva > 0 ? d.aliquota_iva : f.aliquota_iva,
                      data_incasso_prevista: /^\d{4}-\d{2}-\d{2}$/.test(d.data_scadenza) ? d.data_scadenza : f.data_incasso_prevista,
                      note: d.note ? (f.note ? `${f.note}\n${d.note}` : d.note) : f.note,
                    }))}
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="Piattaforma esterna" value={form.piattaforma_esterna} onChange={(v) => setForm({ ...form, piattaforma_esterna: v })} />
                  <Field label="ID/codice esterno" value={form.codice_esterno} onChange={(v) => setForm({ ...form, codice_esterno: v })} />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2 sticky bottom-0 bg-white">
              <button onClick={closeForm} className="px-4 py-2 text-sm border border-slate-300 rounded-sm hover:bg-slate-50">Annulla</button>
              <button
                onClick={() => save.mutate(form)}
                disabled={save.isPending}
                data-testid="btn-save-fattura"
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white font-semibold rounded-sm"
              >
                <Save className="w-4 h-4" /> {save.isPending ? "Salvataggio…" : editing ? "Salva" : "Registra fattura"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KPI({ label, value, highlight }) {
  const bg = {
    blue: "bg-blue-50 border-blue-200 text-blue-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  }[highlight] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`p-3 border rounded-sm ${bg}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
      <div className="font-display font-black text-xl mt-1 truncate">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-600 block mb-1">{label}</label>
      <input type={type} step={step} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
    </div>
  );
}
